'use client'

import { usePathname, useSearchParams } from 'next/navigation'
import React, {
  createContext,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { useAuth } from '@workos-inc/authkit-nextjs/components'

import { isSignedIn } from '@/app/actions/session'
import { EventAuthModal } from '@/components/events/EventAuthModal'

interface EventsFormProviderContextType {
  isCreateForm: boolean
  setIsCreateForm: (isCreateForm: boolean) => void
  editEventId: string | null
  setEditEventId: (editEventId: string | null) => void
  closeForm: () => void
  requestCreateEvent: () => void
}

const EventsFormProviderContext = createContext<EventsFormProviderContextType | undefined>(
  undefined,
)

/**
 * Form state for creating and editing events from anywhere in the layout.
 */
export const useEventsFormProviderContext = () => {
  const context = useContext(EventsFormProviderContext)
  if (!context) {
    throw new Error('useEventsFormProviderContext must be used within a EventsFormProvider')
  }
  return context
}

interface EventsFormQuery {
  hasCreateEventParam: boolean
  editEventParam: string | null
  search: string
}

interface EventsFormProviderProps {
  children: React.ReactNode
}

/**
 * Reads `create-event` and `edit-event` without wrapping the page.
 * `useSearchParams()` bails out of static rendering up to the nearest Suspense
 * boundary, so this leaf is the only thing allowed to suspend.
 */
function EventsFormQueryBridge({ onQuery }: { onQuery: (query: EventsFormQuery) => void }) {
  const searchParams = useSearchParams()
  const hasCreateEventParam = searchParams.get('create-event') !== null
  const editEventParam =
    !hasCreateEventParam && searchParams.get('edit-event') !== null
      ? searchParams.get('edit-event')
      : null
  const search = searchParams.toString()

  useEffect(() => {
    onQuery({ hasCreateEventParam, editEventParam, search })
  }, [editEventParam, hasCreateEventParam, onQuery, search])

  return null
}

/**
 * Keeps the create/edit event form available on every page, including deep links.
 */
export const EventsFormProvider: React.FC<EventsFormProviderProps> = ({ children }) => {
  const pathname = usePathname() ?? ''
  const { user, loading } = useAuth()

  const [queryReady, setQueryReady] = useState(false)
  const [hasCreateEventParam, setHasCreateEventParam] = useState(false)
  const [searchKey, setSearchKey] = useState('')
  const [isCreateForm, setIsCreateFormRaw] = useState(false)
  const [editEventId, setEditEventIdRaw] = useState<string | null>(null)
  const [authModalOpen, setAuthModalOpen] = useState(false)
  const [dismissedCreateEventRedirect, setDismissedCreateEventRedirect] = useState<string | null>(
    null,
  )
  const editSeededRef = useRef(false)

  const applyQuery = useCallback((query: EventsFormQuery) => {
    setQueryReady(true)
    setHasCreateEventParam((prev) =>
      prev === query.hasCreateEventParam ? prev : query.hasCreateEventParam,
    )
    setSearchKey((prev) => (prev === query.search ? prev : query.search))
    if (!editSeededRef.current) {
      editSeededRef.current = true
      if (query.editEventParam) setEditEventIdRaw(query.editEventParam)
    }
  }, [])

  const isAuthReady = !loading
  const needsCreateAuth = isAuthReady && hasCreateEventParam && user === null

  const createEventRedirect = `${pathname}?create-event`

  const shouldAutoOpenFromDeepLink =
    isAuthReady &&
    hasCreateEventParam &&
    user !== null &&
    dismissedCreateEventRedirect !== createEventRedirect

  const effectiveIsCreateForm = isCreateForm || shouldAutoOpenFromDeepLink

  const showAuthModal =
    authModalOpen || (needsCreateAuth && dismissedCreateEventRedirect !== createEventRedirect)

  const setIsCreateForm = useCallback((next: boolean) => {
    if (next) {
      setDismissedCreateEventRedirect(null)
    }
    setIsCreateFormRaw(next)
  }, [])

  const setEditEventId = useCallback((next: string | null) => {
    setEditEventIdRaw(next)
  }, [])

  const closeForm = useCallback(() => {
    setIsCreateFormRaw(false)
    setEditEventId(null)
    setDismissedCreateEventRedirect(createEventRedirect)
  }, [createEventRedirect, setEditEventId])

  const requestCreateEvent = useCallback(() => {
    void (async () => {
      const signedIn = await isSignedIn()
      if (!signedIn) {
        setDismissedCreateEventRedirect(null)
        setAuthModalOpen(true)
        return
      }
      setDismissedCreateEventRedirect(null)
      setIsCreateForm(true)
    })()
  }, [setIsCreateForm])

  const handleAuthModalOpenChange = useCallback(
    (open: boolean) => {
      setAuthModalOpen(open)
      if (!open) {
        if (needsCreateAuth) {
          setDismissedCreateEventRedirect(createEventRedirect)
        }
        // Strip create-event so the auth modal does not immediately reopen
        const params = new URLSearchParams(window.location.search)
        params.delete('create-event')
        const qs = params.toString().replace(/=(?=&|$)/g, '')
        const url = qs ? `${pathname}?${qs}` : pathname
        window.history.replaceState(window.history.state, '', url)
      }
    },
    [createEventRedirect, needsCreateAuth, pathname],
  )

  const handleAuthenticated = useCallback(() => {
    setAuthModalOpen(false)
    setIsCreateForm(true)
  }, [setIsCreateForm])

  useEffect(() => {
    if (!queryReady) return
    const params = new URLSearchParams(window.location.search)
    if (effectiveIsCreateForm) {
      params.set('create-event', '')
    } else if (!showAuthModal) {
      params.delete('create-event')
    }
    const qs = params.toString().replace(/=(?=&|$)/g, '')
    const url = qs ? `${pathname}?${qs}` : pathname
    window.history.replaceState(window.history.state, '', url)
  }, [effectiveIsCreateForm, pathname, queryReady, searchKey, showAuthModal])

  useEffect(() => {
    if (!queryReady) return
    const params = new URLSearchParams(window.location.search)
    if (editEventId && editEventId.length > 0) {
      params.set('edit-event', editEventId)
    } else {
      params.delete('edit-event')
    }
    const qs = params.toString().replace(/=(?=&|$)/g, '')
    const url = qs ? `${pathname}?${qs}` : pathname
    window.history.replaceState(window.history.state, '', url)
  }, [editEventId, pathname, queryReady, searchKey])

  const value: EventsFormProviderContextType = {
    isCreateForm: effectiveIsCreateForm,
    setIsCreateForm,
    editEventId,
    setEditEventId,
    closeForm,
    requestCreateEvent,
  }

  return (
    <EventsFormProviderContext.Provider value={value}>
      {children}
      <Suspense fallback={null}>
        <EventsFormQueryBridge onQuery={applyQuery} />
      </Suspense>
      <EventAuthModal
        open={showAuthModal}
        onOpenChange={handleAuthModalOpenChange}
        redirectTo={createEventRedirect}
        onAuthenticated={handleAuthenticated}
      />
    </EventsFormProviderContext.Provider>
  )
}
